import type { NewlyNotReportingFacilitiesQuery } from "generated-graphql/graphql";

import { Alert, Box, Link, Typography } from "@mui/material";
import { DataGrid, type GridRowParams } from "@mui/x-data-grid";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useOpenNewlyNotReportingReports } from "hooks/useOpenNewlyNotReportingReports";
import pluralize from "pluralize";
import { NEWLY_NOT_REPORTING_FACILITIES_QUERY } from "queries/newlyNotReportingFacilities";
import { useCallback, useEffect, useMemo, useState } from "react";

type NewlyNotReportingFacility =
  NewlyNotReportingFacilitiesQuery["newlyNotReportingFacilities"]["items"][number];

function ReportStatusCell({
  status,
  reportId,
}: {
  status: string | null | undefined;
  reportId: string | null | undefined;
}) {
  if (!status) return null;
  if (!reportId) return <>{status}</>;
  return (
    <Link
      href={`/staff/fulfillment/r/${reportId}/activities`}
      target="_blank"
      rel="noopener"
      onClick={(e) => e.stopPropagation()}
    >
      {status}
    </Link>
  );
}

function getColumns(reportingYear: number): OmnisearchGridColDef<NewlyNotReportingFacility>[] {
  return [
    {
      field: "tenantName",
      headerName: "Organization",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "facilityName",
      headerName: "Facility",
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Link
          href={`/o/${row.tenantId}/facilities/${row.facilityId}/profile`}
          target="_blank"
          rel="noopener"
          onClick={(e) => e.stopPropagation()}
        >
          {row.facilityName}
        </Link>
      ),
    },
    {
      field: "state",
      headerName: "State/Territory",
      width: 70,
      valueGetter: (_, row) => row.state ?? "",
    },
    {
      field: "notReportingReason",
      headerName: "Not Reporting Reason",
      flex: 1,
      minWidth: 140,
      valueGetter: (_, row) => row.notReportingReason ?? "",
    },
    {
      field: "encampStatusPreviousYear",
      headerName: `RY${reportingYear - 1} Status`,
      width: 140,
      renderCell: ({ row }) => (
        <ReportStatusCell
          status={row.encampStatusPreviousYear}
          reportId={row.previousYearReportId}
        />
      ),
    },
    {
      field: "encampStatusCurrentYear",
      headerName: `RY${reportingYear} Status`,
      width: 140,
      renderCell: ({ row }) => (
        <ReportStatusCell status={row.encampStatusCurrentYear} reportId={row.currentYearReportId} />
      ),
    },
    {
      field: "hasNewlyNotReportingReport",
      headerName: "Has NNR Report",
      width: 120,
      renderCell: ({ row }) => {
        if (!row.hasNewlyNotReportingReport || !row.newlyNotReportingReportId) {
          return "No";
        }
        return (
          <Link
            href={`/staff/fulfillment/r/${row.newlyNotReportingReportId}/activities`}
            target="_blank"
            rel="noopener"
            onClick={(e) => e.stopPropagation()}
          >
            Yes
          </Link>
        );
      },
    },
  ];
}

type Props = {
  open: boolean;
  onClose: () => void;
  reportingYear: number;
};

export function NewlyNotReportingFacilitiesDialog({ open, onClose, reportingYear }: Props) {
  const alerts = useAlerts();
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [openNewlyNotReportingReports, { loading }] = useOpenNewlyNotReportingReports();
  const columns = useMemo(() => getColumns(reportingYear), [reportingYear]);

  // Track loaded items so we can show facility details in the confirmation dialog.
  // Use state (not ref) so selectedFacilities has real deps and re-derives correctly.
  const [itemMap, setItemMap] = useState<Map<string, NewlyNotReportingFacility>>(() => new Map());

  // Clear accumulated items whenever the reporting year changes.
  useEffect(() => {
    setItemMap(new Map());
  }, [reportingYear]);

  const getItems = useCallback(
    (data: NewlyNotReportingFacilitiesQuery) => data.newlyNotReportingFacilities.items,
    []
  );

  const getCount = useCallback(
    (data: NewlyNotReportingFacilitiesQuery) => data.newlyNotReportingFacilities.count,
    []
  );

  const handleDataChange = useCallback((data: NewlyNotReportingFacilitiesQuery | undefined) => {
    if (!data) return;
    const items = data.newlyNotReportingFacilities.items;
    setItemMap((prev) => {
      const next = new Map(prev);
      for (const item of items) {
        next.set(item.facilityId, item);
      }
      return next;
    });
  }, []);

  const handleSelectedIdsChanged = useCallback((ids: (string | number)[]) => {
    setSelectedFacilityIds(ids.map(String));
  }, []);

  const selectedFacilities = useMemo(
    () =>
      selectedFacilityIds
        .map((id) => itemMap.get(id))
        .filter((f): f is NewlyNotReportingFacility => f != null),
    [selectedFacilityIds, itemMap]
  );

  const alreadyHaveNnr = selectedFacilities.filter((f) => f.hasNewlyNotReportingReport);
  const willCreate = selectedFacilities.filter((f) => !f.hasNewlyNotReportingReport);

  const handleCreateReports = useCallback(async () => {
    if (selectedFacilityIds.length === 0) return;
    try {
      const result = await openNewlyNotReportingReports({
        variables: { facilityIds: selectedFacilityIds, reportingYear },
      });
      const data = result.data?.openNewlyNotReportingReports;
      const createdCount = data?.createdCount ?? 0;
      const skippedCount = data?.skippedCount ?? 0;
      const message =
        skippedCount > 0
          ? `Opened ${createdCount} newly not reporting ${pluralize("report", createdCount)}. ${skippedCount} ${pluralize("facility", skippedCount)} skipped.`
          : `Opened ${createdCount} newly not reporting ${pluralize("report", createdCount)}.`;
      alerts.success(message);
      setSelectedFacilityIds([]);
      setConfirmOpen(false);
      onClose();
    } catch (error) {
      alerts.error("Error opening newly not reporting reports", error);
    }
  }, [selectedFacilityIds, reportingYear, openNewlyNotReportingReports, alerts, onClose]);

  const hasSelection = selectedFacilityIds.length > 0;
  const primaryLabel = hasSelection
    ? `Create ${selectedFacilityIds.length} ${pluralize("report", selectedFacilityIds.length)}`
    : "Close";

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title="Newly Not Reporting Facilities"
        fullWidth
        maxWidth="xl"
        hideCancelButton={!hasSelection}
        DialogContentProps={{ sx: { px: 0 } }}
        primaryActionButtonProps={{
          saveText: primaryLabel,
          onClick: hasSelection ? () => setConfirmOpen(true) : onClose,
          hideStartIcon: !hasSelection,
        }}
      >
        <Box sx={{ mb: 2, px: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Facilities that were actively filing in RY{reportingYear - 1} but are not reporting in
            RY{reportingYear}. A facility qualifies when:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Had a FILING_COMPLETE annual report in RY{reportingYear - 1}
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Does not have a FILING_COMPLETE annual report in RY{reportingYear}
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Is closed, inactive, under thresholds, not reporting, filed outside Encamp, or has no
              annual report for RY{reportingYear}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Select rows and click Create to open newly not reporting reports for the selected
            facilities.
          </Typography>
        </Box>
        <Box sx={{ minHeight: 400 }}>
          <OmnisearchDataGrid<
            NewlyNotReportingFacilitiesQuery,
            { reportingYear: number },
            NewlyNotReportingFacility
          >
            persistenceKey="newly-not-reporting-facilities-dialog"
            columns={columns}
            dataQuery={NEWLY_NOT_REPORTING_FACILITIES_QUERY}
            dataQueryVariables={{ reportingYear }}
            defaultSearch="notReportingReason:!without"
            getItems={getItems}
            getCount={getCount}
            onDataStateChange={handleDataChange}
            getRowId={(row) => row.facilityId}
            isURLDriven={false}
            skip={!open}
            noDataMessage="No newly not reporting facilities for this year."
            initialPageSize={25}
            rowHeight={40}
            gridSx={{
              minHeight: 400,
              maxHeight: "60vh",
            }}
            checkboxSelection
            isRowSelectable={(params: GridRowParams<NewlyNotReportingFacility>) =>
              params.row.notReportingReason !== "Without Encamp"
            }
            selectedIds={selectedFacilityIds}
            onSelectedIdsChanged={handleSelectedIdsChanged}
          />
        </Box>
      </Dialog>
      <ConfirmDialog
        open={confirmOpen}
        loading={loading}
        onConfirm={handleCreateReports}
        onClose={() => setConfirmOpen(false)}
        header="Create Newly Not Reporting Reports"
        confirmText={`Create ${willCreate.length} ${pluralize("report", willCreate.length)}`}
        cancelText="Cancel"
        maxWidth="700px"
        disableConfirm={willCreate.length === 0}
      >
        <Typography variant="body2" sx={{ mb: 2 }}>
          Do you want to create newly not reporting reports for the following {willCreate.length}{" "}
          {pluralize("facility", willCreate.length)}?
        </Typography>
        {alreadyHaveNnr.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {alreadyHaveNnr.length} {pluralize("facility", alreadyHaveNnr.length)} already{" "}
            {alreadyHaveNnr.length === 1 ? "has" : "have"} an NNR report and will be skipped.
          </Alert>
        )}
        <DataGrid
          rows={selectedFacilities}
          getRowId={(row) => row.facilityId}
          columns={[
            { field: "tenantName", headerName: "Organization", flex: 1, minWidth: 120 },
            { field: "facilityName", headerName: "Facility", flex: 1, minWidth: 120 },
            { field: "state", headerName: "State/Territory", width: 70 },
            {
              field: "status",
              headerName: "Status",
              width: 100,
              valueGetter: (_, row) =>
                row.hasNewlyNotReportingReport ? "(skipped)" : "Will create",
            },
          ]}
          density="compact"
          hideFooter={selectedFacilities.length <= 10}
          pageSizeOptions={[10]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          sx={{ maxHeight: 400 }}
          disableRowSelectionOnClick
          disableColumnMenu
        />
      </ConfirmDialog>
    </>
  );
}
